import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { T, Var, useGT } from 'gt-react';
import {
  ArrowLeftIcon,
  FilePdfIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import type { AllPricelistDoc } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PricelistCsvGrid } from './PricelistCsvGrid';
import { PricelistCsvWindowedView } from './PricelistCsvWindowedView';
import { PricelistSearchBox } from './PricelistSearchBox';
import { PricelistSearchResults } from './PricelistSearchResults';
import { useLoadedPricelistSearch } from './usePricelistSearch';
import { usePricelistListSearch } from './usePricelistListSearch';
import { buildCsvDrafts } from './pricelistCsvEditState';
import { pricelistTableKey, pricelistTableLabel } from './pricelistTableLabel';
import {
  pricelistDocOwner,
  pricelistDocSubtitle,
} from './pricelistDocSubtitle';

/** Boxed message for the panel's loading/empty/error states. */
function PanelNotice({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'destructive';
}) {
  return (
    <div
      className={
        tone === 'destructive'
          ? 'px-3 py-2 text-sm text-destructive'
          : 'px-3 py-2 text-sm text-muted-foreground'
      }
    >
      {children}
    </div>
  );
}

/** One pricelist in the searchable list: name, case number, company, date. */
function PricelistListRow({
  doc,
  onSelect,
}: {
  doc: AllPricelistDoc;
  onSelect: (doc: AllPricelistDoc) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(doc)}
      className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
    >
      <FilePdfIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{doc.name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {pricelistDocSubtitle(doc)}
        </span>
      </span>
    </button>
  );
}

/** The searchable pricelist list — the panel's landing view. */
function PricelistSearchList({
  onSelect,
}: {
  onSelect: (doc: AllPricelistDoc) => void;
}) {
  const gt = useGT();
  const { search, setSearch, listQuery, documents, totalCount } =
    usePricelistListSearch();

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* h-9 row + h-px divider mirrors the tab strip's toggle button, so the
          two separators meet at the panel edge. */}
      <div className="flex h-9 shrink-0 items-center px-1.5">
        <div className="relative w-full">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={gt('Search pricelists or PC number…', {
              context:
                'placeholder for the pricelist comparison panel search; PC number is the price-change case number, e.g. PC-12',
            })}
            className="h-7 pl-8"
          />
        </div>
      </div>
      <div className="h-px shrink-0 bg-border" />

      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {listQuery.isPending ? (
          <div className="space-y-2 p-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ) : listQuery.isError ? (
          <PanelNotice tone="destructive">
            {gt('Failed to load pricelists.', {
              context: 'error state for the pricelist comparison panel list',
            })}
          </PanelNotice>
        ) : documents.length === 0 ? (
          <PanelNotice>
            <T context="empty state for the pricelist comparison panel list">
              No pricelists found.
            </T>
          </PanelNotice>
        ) : (
          documents.map((doc) => (
            <PricelistListRow key={doc.id} doc={doc} onSelect={onSelect} />
          ))
        )}
      </div>

      {totalCount > documents.length && (
        <div className="shrink-0 border-t px-3 py-1.5 text-xs text-muted-foreground">
          <T context="footer of the pricelist comparison panel list when only the first page is shown">
            Showing <Var>{documents.length}</Var> of <Var>{totalCount}</Var> —
            search to narrow.
          </T>
        </div>
      )}
    </div>
  );
}

/**
 * One selected pricelist's extracted tables, read-only, one tab per table.
 * Cells select and copy out through Handsontable's CopyPaste plugin, so ranges
 * paste straight into Excel next to catalog data for vlookup-style comparisons.
 */
function SelectedPricelistView({
  doc,
  onBack,
}: {
  doc: AllPricelistDoc;
  onBack: () => void;
}) {
  const gt = useGT();
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState('');

  // The list row's csvDocuments were captured when the list was fetched, so an
  // extraction finishing (or a re-extract) after that would go unseen. Resolve
  // the current CSV document from the pricelist itself on every selection.
  const pricelistQuery = useQuery(
    orpc.inventoryManagement.pricelist.get.queryOptions({
      input: { pdfDocumentId: doc.id },
    }),
  );
  const csvDocumentId = pricelistQuery.data?.csvDocuments[0]?.id;

  const csvQuery = useQuery({
    ...orpc.inventoryManagement.pricelist.getExtractedCsv.queryOptions({
      input: { documentId: csvDocumentId ?? '' },
    }),
    enabled: csvDocumentId !== undefined,
  });

  // Read-only, so the drafts never diverge from the fetch; rebuilt only when a
  // new fetch lands.
  const drafts = useMemo(
    () => buildCsvDrafts(csvQuery.data?.tables ?? []),
    [csvQuery.data],
  );
  // A refetch can come back with fewer tables than the open tab.
  const tableIndex = Math.min(activeIndex, Math.max(drafts.length - 1, 0));
  const activeDraft = drafts[tableIndex];

  const search = useLoadedPricelistSearch({ tables: drafts, query });
  const owner = pricelistDocOwner(doc);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* h-9 row + h-px divider mirrors the tab strip's toggle button, so the
          two separators meet at the panel edge. */}
      <div className="flex h-9 shrink-0 items-center gap-1.5 px-1.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onBack}
          aria-label={gt('Back to pricelist list', {
            context:
              'aria label for the pricelist comparison panel back button',
          })}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <span className="truncate text-sm font-medium">{doc.name}</span>
        {owner && (
          <span className="shrink-0 truncate text-xs text-muted-foreground">
            · {owner}
          </span>
        )}
      </div>
      <div className="h-px shrink-0 bg-border" />

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {pricelistQuery.isPending ||
        (csvDocumentId !== undefined && csvQuery.isPending) ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        ) : pricelistQuery.isError ? (
          <PanelNotice tone="destructive">
            {gt('Failed to load the pricelist.', {
              context: 'error state for the pricelist comparison panel',
            })}
          </PanelNotice>
        ) : csvDocumentId === undefined ? (
          <PanelNotice>
            <T context="empty state when a pricelist has no extracted CSV to compare against">
              This pricelist has no extracted table data yet.
            </T>
          </PanelNotice>
        ) : csvQuery.isError ? (
          <PanelNotice tone="destructive">
            {gt('Failed to load the pricelist tables.', {
              context: 'error state for the pricelist comparison panel tables',
            })}
          </PanelNotice>
        ) : !activeDraft ? (
          <PanelNotice>
            <T context="empty state when a pricelist CSV has no table data">
              No tabular data found in this pricelist.
            </T>
          </PanelNotice>
        ) : csvQuery.data && !csvQuery.data.complete ? (
          // Too large to return whole; page through it read-only. Ranges still
          // copy out, one page at a time.
          <PricelistCsvWindowedView
            csvDocumentId={csvDocumentId}
            tables={csvQuery.data.tables}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <PricelistSearchBox
              value={query}
              onChange={setQuery}
              isSearching={false}
            />
            {drafts.length > 1 && (
              <Tabs
                value={String(tableIndex)}
                onValueChange={(value) => setActiveIndex(Number(value))}
              >
                <TabsList>
                  {drafts.map((draft, index) => (
                    <TabsTrigger
                      key={pricelistTableKey(draft.name, draft.headers)}
                      value={String(index)}
                    >
                      {pricelistTableLabel(gt, draft.name, index)}
                      {search.hitCounts[index] === undefined ? null : (
                        <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] tabular-nums">
                          {search.hitCounts[index].toLocaleString()}
                        </span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            {search.searching ? (
              <PricelistSearchResults
                view={search}
                tableIndex={tableIndex}
                query={query}
              />
            ) : null}
            {/* Hidden rather than unmounted, so clearing the search doesn't
                rebuild Handsontable over the whole table. */}
            <div className={search.searching ? 'hidden' : undefined}>
              <PricelistCsvGrid
                key={`${doc.id}:${tableIndex}`}
                draft={activeDraft}
                dataVersion={0}
                readOnly
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The pricelist comparison side panel: a searchable list of uploaded
 * pricelists, and — once one is picked — its extracted tables as read-only,
 * copyable spreadsheets for eyeballing or exporting against catalog data.
 */
export function PricelistComparisonPanel() {
  const [selected, setSelected] = useState<AllPricelistDoc | null>(null);

  return (
    <div className="h-full min-h-0 bg-card">
      {selected ? (
        <SelectedPricelistView
          doc={selected}
          onBack={() => setSelected(null)}
        />
      ) : (
        <PricelistSearchList onSelect={setSelected} />
      )}
    </div>
  );
}
